const DEFAULT_MODEL = 'deepseek-ai/deepseek-v4.1-flash';
const DEFAULT_BASE_URL = 'https://integrate.api.nvidia.com';
const REQUEST_TIMEOUT_MS = 15000;

function normalizeBase(baseUrl, fallback = '') {
  const raw = (baseUrl || fallback || '').trim();
  if (!raw) return '';
  const withoutSlash = raw.replace(/\/$/, '');
  const withoutV1 = withoutSlash.replace(/\/v1$/i, '');
  return withoutV1.replace(/\/$/, '');
}

function buildPrompt(systemPrompt, context) {
  const segments = [systemPrompt || ''];
  if (context) {
    segments.push(`Match context:\n${context}`);
  }
  return segments.filter(Boolean).join('\n\n');
}

export async function sendHaxGptChat({ systemPrompt, context, messages }) {
  const proxyBase = normalizeBase(
    process.env.REACT_APP_HAXGPT_PROXY_URL
      || process.env.REACT_APP_RECORDING_SERVER_URL
      || process.env.HAXGPT_PROXY_URL
  );
  const baseUrl = normalizeBase(
    process.env.REACT_APP_NVIDIA_NIM_API_URL
      || process.env.NVIDIA_API_BASE_URL,
    DEFAULT_BASE_URL
  );
  const apiKey = (process.env.REACT_APP_NVIDIA_NIM_API_KEY || process.env.NVIDIA_API_KEY || '').trim();
  const model = (process.env.REACT_APP_NVIDIA_NIM_MODEL
    || process.env.NVIDIA_HAXGPT_MODEL
    || process.env.NVIDIA_RATINGS_MODEL
    || DEFAULT_MODEL).trim();

  const convo = Array.isArray(messages) ? messages.map((message) => ({
    role: typeof message?.role === 'string' ? message.role : 'user',
    content: String(message?.content ?? '').slice(0, 4000)
  })).filter((entry) => entry.content.length) : [];

  const prompt = buildPrompt(systemPrompt, context);
  const systemMessage = {
    role: 'system',
    content: prompt || 'You are Hax GPT, a constructive Haxball coach.'
  };

  const payload = {
    model,
    messages: [systemMessage, ...convo],
    temperature: 0.4,
    top_p: 0.9,
    max_tokens: 512
  };

  if (proxyBase) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(`${proxyBase}/api/haxgpt/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          systemPrompt,
          context,
          messages: convo,
          model,
          temperature: payload.temperature,
          topP: payload.top_p,
          maxTokens: payload.max_tokens
        }),
        signal: controller.signal
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Hax GPT timed out while waiting for the model. Try again in a moment.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Hax GPT proxy failed with status ${response.status}`);
    }

    const data = await response.json().catch(() => null);
    if (data && typeof data.content === 'string') {
      return data.content.trim();
    }
    if (data && data.error) {
      throw new Error(typeof data.error === 'string' ? data.error : 'Hax GPT proxy error.');
    }
    return '';
  }

  if (!apiKey) {
    throw new Error('Hax GPT is not configured. Set a proxy URL or the NVIDIA NIM API key.');
  }

  let response;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    response = await fetch(`${baseUrl}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Hax GPT timed out while waiting for the model. Try again in a moment.');
    }
    const message = err && typeof err.message === 'string' ? err.message : '';
    if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('TypeError')) {
      throw new Error('Hax GPT request blocked by the browser (CORS). Configure a proxy endpoint.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    if (response.type === 'opaque') {
      throw new Error('Hax GPT request blocked by the browser (CORS). Configure a proxy endpoint.');
    }
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `NIM request failed with status ${response.status}`);
  }

  const data = await response.json().catch(() => null);
  const text = data?.choices?.[0]?.message?.content || '';
  return text.trim();
}
